import { Paper, SimpleGrid, Skeleton, Stack } from '@cowbell/ui';
import styles from './StudioSkeleton.module.css';

export type StudioSkeletonVariant = 'cards' | 'rows' | 'detail';

interface StudioSkeletonProps {
  readonly variant: StudioSkeletonVariant;
  /** Number of placeholder blocks. Default 4. Ignored for `variant="detail"`,
   *  which always renders one fixed header + 3 paragraph bars. */
  readonly count?: number;
}

const DEFAULT_COUNT = 4;
const DETAIL_PARAGRAPH_COUNT = 3;

/** `cards` — 2-col grid of icon+2-line blocks, mirroring the Agents grid
 *  (AgentCard: avatar + name + description). */
function CardsSkeleton({ count }: Readonly<{ count: number }>) {
  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      {Array.from({ length: count }, (_, index) => (
        <Paper key={index} withBorder p="md" data-skeleton-card>
          <div className={styles.cardRow}>
            <Skeleton height={40} width={40} circle />
            <div className={styles.cardLines}>
              <Skeleton height={14} width="70%" />
              <Skeleton height={10} width="95%" mt={8} />
            </div>
          </div>
        </Paper>
      ))}
    </SimpleGrid>
  );
}

/** `rows` — full-width title+description bars in a bordered row, mirroring
 *  the Skills/Integrations list items. */
function RowsSkeleton({ count }: Readonly<{ count: number }>) {
  return (
    <Stack gap="md">
      {Array.from({ length: count }, (_, index) => (
        <Paper key={index} withBorder p="lg" data-skeleton-row>
          <Skeleton height={14} width="35%" />
          <Skeleton height={10} width="80%" mt={10} />
        </Paper>
      ))}
    </Stack>
  );
}

/** `detail` — header block (title + subtitle bars) plus a bordered panel of
 *  3 paragraph bars, mirroring a detail page's header + description panel. */
function DetailSkeleton() {
  return (
    <Stack gap="lg" data-skeleton-detail>
      <Stack gap="xs" data-skeleton-header>
        <Skeleton height={22} width="40%" />
        <Skeleton height={12} width="60%" />
      </Stack>
      <Paper withBorder p="lg">
        <Stack gap="sm">
          {Array.from({ length: DETAIL_PARAGRAPH_COUNT }, (_, index) => (
            <Skeleton key={index} height={12} data-skeleton-paragraph />
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}

/**
 * One shared loading placeholder for the Moo Studio surface, replacing the
 * bare `<Loader/>` spinners (and a couple of literal "Loading…" strings)
 * every list/detail view used to render individually. `variant` picks the
 * shape that matches the surface's loaded content so the placeholder doesn't
 * visually jump when the real data arrives:
 * - `cards` — the Agents / Integration-catalog / Tool-category grid
 * - `rows` — the Skills / Integrations / Custom-Skills / Tasks list
 * - `detail` — a single-entity detail page (Tool/Skill/Agent/Integration/Task)
 *
 * Built on `@cowbell/ui`'s `Skeleton` (itself Mantine's), so it already gets
 * scheme-aware light/dark shimmer colors for free. The shimmer keyframe is
 * disabled under `prefers-reduced-motion` in the accompanying CSS module.
 */
export default function StudioSkeleton({
  variant,
  count = DEFAULT_COUNT,
}: StudioSkeletonProps) {
  return (
    <div
      className={styles.root}
      role="status"
      aria-label="Loading"
      data-skeleton-variant={variant}
    >
      {variant === 'cards' && <CardsSkeleton count={count} />}
      {variant === 'rows' && <RowsSkeleton count={count} />}
      {variant === 'detail' && <DetailSkeleton />}
    </div>
  );
}
